import React, {useEffect, useState} from 'react';
import Typography from '@mui/material/Typography';
import {Box, Button, Card, Checkbox, CircularProgress, FormControl, IconButton, InputAdornment, InputLabel, MenuItem, Select, styled, TextField} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import moment from 'moment';
import {DatePicker} from '@mui/x-date-pickers';
import {useDispatch, useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import PropTypes from 'prop-types';
import {STUDENT_PROFILE_CONFIRMATION_TYPES} from '../../const/StudentProfileConfirmationTypes';
import ConfirmationWrapper from '../../Components/ConfirmationWrapper';
import {useGetFreeSchoolMealsQuery, useManageFreeSchoolMealsMutation, useSyncFreeSchoolMealsToMisMutation} from '../../../../../api/services/studentService';
import {useGetUkCountriesQuery} from '../../../../../api/services/lookupsService';
import useUser from '../../../../../utility/hooks/useUser';
import {validateNINumber} from '../../../../../utility/validationUtil';
import AdmicityDialog from '../../../../../shared-components/AdmicityDialog';
import {retrieveBasicInfo} from '../../ProfilesSlice';
import {getHisHerPronoun} from '../../../../../utility/pronounUtil';
import {
    MAX_FSM_CONTACTS,
    areFsmContactsValid,
    FSM_IN_RECEIPT,
    contactsPayloadForSave,
    fsmInReceiptMode,
    normalizeFsmContactForCompare,
    shouldShowFsmContacts,
    toFsmContactDateOnly,
} from './freeSchoolMealsContactsValidation';
import {toDateMoment, toDateOnly} from '../../../../../utility/dateUtil';
import {MASKED_TEXT, RevealButton, hasMaskableValue} from '../../../../../shared-components/MaskedValue';

export const Item = styled(Box)({
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingTop: 2
});

// Maria, 18 October 2026: each tick before its text (the checkbox is the second child, so the row is reversed)
// Maria, 18 October 2026: the ticks in an outlined box, one per row with a line between them, as on Welfare
const TICK_CARD_SX = {borderRadius: 2, '& > *': {px: 2, py: 0.5}, '& > * + *': {borderTop: '1px solid #eef0f3'}};

const TickRow = styled(Box)({
    display: 'flex',
flexDirection: 'row-reverse',
justifyContent: 'flex-end',
alignItems: 'center',
gap: 8,
    '& .MuiCheckbox-root': {marginTop: 0, flexShrink: 0}
});

const MAX_CONTACTS = MAX_FSM_CONTACTS;

const emptyContact = () => ({
    fullName: '',
    dateOfBirth: null,
    insuranceNumber: '',
    insuranceNumberShown: false,
    fullNameError: false,
    dateOfBirthError: false,
    insuranceNumberError: '',
});

const mapApiContacts = (contacts) => {
    if (!Array.isArray(contacts) || contacts.length === 0) {
        return [emptyContact()];
    }

    return contacts.map((contact) => ({
        fullName: contact.fullName ?? '',
        dateOfBirth: toFsmContactDateOnly(contact.dateOfBirth),
        insuranceNumber: contact.insuranceNumber ?? '',
        insuranceNumberShown: false,
        fullNameError: false,
        dateOfBirthError: false,
        insuranceNumberError: '',
    }));
};

const isInsuranceNumberMasked = contact => hasMaskableValue(contact.insuranceNumber) && !contact.insuranceNumberShown;

const FreeSchoolMealsFields = ({readonly, onConfirmSuccess}) => {
    const [currentlyFree, setCurrentlyFree] = useState(false);
    const [lastFree, setLastFree] = useState(false);
    const [youngCarer, setYoungCarer] = useState(false);
    const [serviceChild, setServiceChild] = useState(false);
    const [contacts, setContacts] = useState([emptyContact()]);
    const [isNotApplicable, setIsNotApplicable] = useState(undefined);
    const [syncDialogOpen, setSyncDialogOpen] = useState(false);
    const [syncStartDate, setSyncStartDate] = useState(null);
    const [syncEndDate, setSyncEndDate] = useState(null);
    const [syncCountryId, setSyncCountryId] = useState('');
    const [syncNote, setSyncNote] = useState('');
    const [syncStartDateError, setSyncStartDateError] = useState(false);
    const [syncStartDateBeforeDobError, setSyncStartDateBeforeDobError] = useState(false);
    const dispatch = useDispatch();
    const {user} = useUser();
    const {selectedProfile, currentSchool} = useSelector(state => state.profilesInfo);
    const {studentId} = useParams();
    const [updateFreeSchoolMeals] = useManageFreeSchoolMealsMutation();
    const [syncToMis, {isLoading: isSyncToMisLoading}] = useSyncFreeSchoolMealsToMisMutation();
    const {
        data: freeSchoolMeals = {options: {}, contacts: []},
        refetch: refetchFreeSchoolMealsData,
    } = useGetFreeSchoolMealsQuery(studentId);
    const {data: ukCountries = []} = useGetUkCountriesQuery(undefined, {skip: !syncDialogOpen});

    const syncedToMis = !!freeSchoolMeals.syncedToMis;
    const isSchoolRole = user?.hasElevatedAccess?.() ?? false;
    const isSectionConfirmed = !!freeSchoolMeals.confirmedAt;
    const showSyncToMisButton = isSectionConfirmed && currentlyFree && !readonly && isSchoolRole && !syncedToMis;
    // Parent contacts for eligibility checks (consent-to-check / last 6 years), and, AD-2646, with "currently in
    // receipt" when the school asks an applicant's parents for them.
    const inReceipt = freeSchoolMeals.inReceiptContacts;
    const showContacts = shouldShowFsmContacts({lastFree, isNotApplicable, currentlyFree, inReceipt});
    // AD-2646: with "currently in receipt" and the school's 'Optional' setting, the parent's details may be left blank.
    const contactsOptional = !lastFree && !isNotApplicable && !!currentlyFree &&
        fsmInReceiptMode(inReceipt) === FSM_IN_RECEIPT.optional;

    useEffect(() => {
        setCurrentlyFree(freeSchoolMeals.currentlyFreeMeals);
        setLastFree(freeSchoolMeals.lastFreeMeals);
        setYoungCarer(!!freeSchoolMeals.youngCarer);
        setServiceChild(!!freeSchoolMeals.serviceChild);
        setContacts(mapApiContacts(freeSchoolMeals.contacts));
        setIsNotApplicable(freeSchoolMeals.isNotApplicable);
    }, [freeSchoolMeals]);

    const handleSave = async () => {
        const body = {
            currentlyFreeMeals: currentlyFree,
            lastFreeMeals: lastFree,
            youngCarer,
            serviceChild,
            isNotApplicable,
            studentId,
            contacts: contactsPayloadForSave({
                lastFree,
                isNotApplicable,
                currentlyFree,
                inReceipt,
                contacts,
            }),
        };

        await updateFreeSchoolMeals({studentId, body});
    };

    const normalizeContactForCompare = normalizeFsmContactForCompare;

    const isEdited = () => {
        const flagsEdited = freeSchoolMeals.currentlyFreeMeals !== currentlyFree ||
            freeSchoolMeals.lastFreeMeals !== lastFree ||
            !!freeSchoolMeals.youngCarer !== youngCarer ||
            !!freeSchoolMeals.serviceChild !== serviceChild ||
            isNotApplicable !== freeSchoolMeals.isNotApplicable;

        const savedContacts = mapApiContacts(freeSchoolMeals.contacts).map(normalizeContactForCompare);
        const currentContacts = contacts.map(normalizeContactForCompare);
        const contactsEdited = JSON.stringify(savedContacts) !== JSON.stringify(currentContacts);

        return flagsEdited || contactsEdited;
    };

    const isValid = () => areFsmContactsValid({
        lastFree,
        isNotApplicable,
        currentlyFree,
        inReceipt,
        contacts,
    });

    const updateContact = (index, patch) => {
        setContacts((prev) => prev.map((contact, i) => (i === index ? {...contact, ...patch} : contact)));
    };

    const addContact = () => {
        if (contacts.length >= MAX_CONTACTS) return;
        setContacts((prev) => [...prev, emptyContact()]);
    };

    const removeContact = (index) => {
        if (contacts.length <= 1) return;
        setContacts((prev) => prev.filter((_, i) => i !== index));
    };

    const studentDob = selectedProfile?.basicInfo?.dateOfBirth ? moment(selectedProfile.basicInfo.dateOfBirth) : null;

    const openSyncDialog = () => {
        if (!selectedProfile?.basicInfo?.dateOfBirth) {
            dispatch(retrieveBasicInfo({studentId, isParent: user?.isParent?.() ?? false}));
        }
        setSyncStartDate(null);
        setSyncEndDate(null);
        setSyncCountryId('');
        setSyncNote('');
        setSyncStartDateError(false);
        setSyncStartDateBeforeDobError(false);
        setSyncDialogOpen(true);
    };

    const closeSyncDialog = () => {
        setSyncDialogOpen(false);
    };

    const isSyncFormValid = () => {
        if (!syncStartDate || !syncCountryId) return false;
        if (studentDob && moment(syncStartDate).isBefore(studentDob, 'day')) return false;
        if (syncEndDate && syncStartDate && !moment(syncEndDate).isAfter(moment(syncStartDate))) return false;
        return true;
    };

    const handleSyncToMis = async () => {
        if (!isSyncFormValid()) {
            setSyncStartDateError(!syncStartDate);
            setSyncStartDateBeforeDobError(!!(syncStartDate && studentDob && moment(syncStartDate).isBefore(studentDob, 'day')));
            return;
        }
        await syncToMis({
            studentId,
            body: {
                startDate: syncStartDate ? moment(syncStartDate).format('YYYY-MM-DD') : null,
                endDate: syncEndDate ? moment(syncEndDate).format('YYYY-MM-DD') : null,
                country: syncCountryId,
                note: syncNote?.trim() ? syncNote.trim().slice(0, 1000) : null
            }
        });
        await refetchFreeSchoolMealsData();
        closeSyncDialog();
    };

    return (
        freeSchoolMeals && <ConfirmationWrapper
                readonly={readonly}
                confirmData={{
                    confirmMessage: `I confirm that the information in the pupil premium and young carers section of ${selectedProfile.name} ${selectedProfile.surname}'s student profile is correct and understand it will be relied upon by school staff and any other person or organisation who may have ${selectedProfile.name} ${selectedProfile.surname} in their care on behalf of ${currentSchool.name}`,
                    confirmedAt: freeSchoolMeals.confirmedAt,
                    confirmedBy: freeSchoolMeals.confirmedBy,
                    enableSaveButton: isEdited() && isValid(),
                    enableConfirmButton: (!isSectionConfirmed || isEdited()) && isValid(),
                    studentId,
                    type: STUDENT_PROFILE_CONFIRMATION_TYPES.FreeSchoolMeals,
                    handleSave,
                    onConfirmSuccess: async () => {
                        await onConfirmSuccess();
                        await refetchFreeSchoolMealsData();
                    },
                    confirmButtonText: 'Confirm'
                }}
                additionalActions={showSyncToMisButton
                    ? (
                        <Button variant="outlined" color="primary" onClick={openSyncDialog}>
                            Sync to MIS
                        </Button>
                    )
                    : null}
                // Maria, 18 October 2026: the confirm box below the section, on the left, as on Dietary
                actionsBelow
                >
                {/* Maria, 17 October 2026: no subheadings here, as these are part of Welfare. The free school meals
                    question, then Service Children, then Young Carers, with space between them. */}
            <Typography variant="body1">Changes to {`${selectedProfile.name}'s`} Free School Meal status can be updated in this section
                    of {`${selectedProfile.name}'s `}
                    Student Profile at any time. This section can be confirmed with none of the boxes ticked.</Typography>
                <Box key={`FreeMeals-${currentlyFree}-${lastFree}`} marginTop={1.5}>
                    <Card variant="outlined" sx={TICK_CARD_SX}>
                    <TickRow>
                        <Typography>{selectedProfile.name} does not currently receive Free School Meals. I give consent
                            for {currentSchool.name} to check {getHisHerPronoun(selectedProfile.gender)} eligibility.</Typography>
                        <Checkbox
                            sx={{width: '25px', height: '25px'}}
                            value={isNotApplicable}
                            checked={!!isNotApplicable}
                            disabled={readonly || (currentlyFree || lastFree)}
                            onChange={event => setIsNotApplicable(event.target.checked)}
                        />
                    </TickRow>
                    <TickRow>
                        <Typography>{selectedProfile.name} is currently in receipt of Free
                            School
                            Meals.</Typography>
                        <Checkbox
                            sx={{width: '25px', height: '25px'}}
                            checked={currentlyFree}
                            disabled={readonly || (isNotApplicable || lastFree)}
                            onChange={event => setCurrentlyFree(event.target.checked)}
                        />
                    </TickRow>
                    <TickRow>
                        <Typography>{selectedProfile.name} has received Free School Meals in the
                            last 6
                            years
                            but
                            is no longer eligible.</Typography>
                        <Checkbox
                            sx={{width: '25px', height: '25px'}}
                            checked={lastFree}
                            disabled={readonly || (currentlyFree || isNotApplicable)}
                            onChange={event => setLastFree(event.target.checked)}
                        />
                    </TickRow>
                    </Card>
                    {showContacts && <Box marginTop={2} display="flex" flexDirection="column" gap={3}>
                        {contacts.map((contact, index) => (
                            <Box key={`fsm-contact-${index}`} display="flex" flexDirection="column" gap={2}>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography variant="subtitle1">Parent {index + 1}</Typography>
                                    {!readonly && contacts.length > 1 && (
                                        <IconButton
                                            aria-label={`Remove parent ${index + 1}`}
                                            onClick={() => removeContact(index)}
                                            size="small"
                                        >
                                            <DeleteOutlineIcon/>
                                        </IconButton>
                                    )}
                                </Box>
                                <TextField
                                    fullWidth
                                    label={'Parent Full Name'}
                                    value={contact.fullName}
                                    required={!contactsOptional}
                                    error={contact.fullNameError}
                                    helperText={contact.fullNameError ? 'This field is required' : ''}
                                    autoComplete="off"
                                    disabled={readonly}
                                    onChange={event => {
                                        if (event.target.value.length <= 100) {
                                            updateContact(index, {
                                                fullName: event.target.value,
                                                fullNameError: !contactsOptional && !event.target.value,
                                            });
                                        }
                                    }}
                                />
                                <Box display={'flex'} gap={3}>
                                    <DatePicker
                                        variant={'fulfilled'}
                                        label={'Parent Date of Birth'}
                                        value={contact.dateOfBirth ? toDateMoment(contact.dateOfBirth) : null}
                                        onChange={value => {
                                            updateContact(index, {dateOfBirth: toDateOnly(value)});
                                        }}
                                        onError={(errorMessage) => {
                                            updateContact(index, {dateOfBirthError: !!errorMessage});
                                        }}
                                        slotProps={{
                                            textField: {
                                                required: !contactsOptional
                                            }
                                        }}
                                        sx={{width: '100%', my: 0}}
                                        maxDate={moment(new Date())}
                                        required={!contactsOptional}
                                        disabled={readonly}
                                        format={'D-MMM-YYYY'}
                                    />
                                    <TextField
                                        fullWidth
                                        label={'Parent National Insurance Number'}
                                        value={isInsuranceNumberMasked(contact) ? MASKED_TEXT : contact.insuranceNumber}
                                        required={!contactsOptional}
                                        error={!!contact.insuranceNumberError}
                                        helperText={contact.insuranceNumberError}
                                        onChange={event => {
                                            if (event.target.value.length <= 20) {
                                                const value = event.target.value;
                                                const validation = validateNINumber(value);

                                                updateContact(index, {
                                                    insuranceNumber: value,
                                                    insuranceNumberShown: true,
                                                    insuranceNumberError: !value
                                                        ? (contactsOptional ? '' : 'This field is required')
                                                        : validation.isValid ? '' : validation.message,
                                                });
                                            }
                                        }}
                                        autoComplete="off"
                                        disabled={readonly}
                                        InputProps={{
                                            readOnly: isInsuranceNumberMasked(contact),
                                            endAdornment: hasMaskableValue(contact.insuranceNumber)
                                                ? (
                                                    <InputAdornment position="end">
                                                        <RevealButton
                                                            label="Parent National Insurance Number"
                                                            revealed={!isInsuranceNumberMasked(contact)}
                                                            onToggle={() => updateContact(index, {insuranceNumberShown: isInsuranceNumberMasked(contact)})}
                                                        />
                                                    </InputAdornment>
                                                )
                                                : undefined
                                        }}
                                    />
                                </Box>
                            </Box>
                        ))}
                        {!readonly && contacts.length < MAX_CONTACTS && (
                            <Box>
                                <Button variant="outlined" onClick={addContact}>
                                    Add parent
                                </Button>
                            </Box>
                        )}
                    </Box>}
                    <Card variant="outlined" sx={{...TICK_CARD_SX, mt: 1.5}}>
                    <TickRow>
                        <Box>
                            <Typography>A Service Child in Education is a child whose
                                parents are Service personnel serving in regular HM Forces military units,
                                or in Armed Forces of another nation and stationed in England. Please tick this box if, having read the above definition,
                                {` ${selectedProfile.name} `}meets the criteria of a Service Child in
                                Education</Typography>
                        </Box>
                        <Checkbox
                            sx={{width: '25px', height: '25px'}}
                            checked={serviceChild}
                            disabled={readonly}
                            onChange={event => setServiceChild(event.target.checked)}
                        />
                    </TickRow>
                    <TickRow>
                        <Box>
                            <Typography>A Young Carer is a person under 18 who regularly provides
                                emotional and/or practical support and assistance for a family member who is disabled,
                                physically or mentally unwell or who misuses substances. Please tick this box if, having read the above definition, you
                                consider{` ${selectedProfile.name} `}to be a Young Carer</Typography>
                        </Box>
                        <Checkbox
                            sx={{width: '25px', height: '25px'}}
                            checked={youngCarer}
                            disabled={readonly}
                            onChange={event => setYoungCarer(event.target.checked)}
                        />
                    </TickRow>
                    </Card>
                </Box>
                <AdmicityDialog
                    open={syncDialogOpen}
                    handleClose={closeSyncDialog}
                    title="Sync to MIS"
                    contentText="Enter the details for syncing Free School Meals to MIS."
                    maxWidth="sm"
                    actions={[
                        {label: 'Cancel', onClick: closeSyncDialog},
                        {
                            label: 'Sync',
                            disabled: isSyncToMisLoading || !isSyncFormValid(),
                            startIcon: isSyncToMisLoading ? <CircularProgress color="inherit" size={16} /> : undefined,
                            onClick: handleSyncToMis
                        }
                    ]}
                >
                    <Box display="flex" flexDirection="column" gap={2} sx={{mt: 1}}>
                        <DatePicker
                            variant="fulfilled"
                            label="Start date *"
                            value={syncStartDate ? moment(syncStartDate) : null}
                            minDate={studentDob || undefined}
                            onChange={value => {
                                setSyncStartDate(value);
                                setSyncStartDateError(false);
                                setSyncStartDateBeforeDobError(false);
                                if (syncEndDate && value && moment(syncEndDate).isSameOrBefore(moment(value))) {
                                    setSyncEndDate(null);
                                }
                            }}
                            onError={(errorMessage) => setSyncStartDateError(!!errorMessage)}
                            slotProps={{
                                textField: {
                                    helperText: syncStartDateBeforeDobError
                                        ? "Start date cannot be before the student's date of birth"
                                        : syncStartDateError
                                            ? 'Start date is required'
                                            : '',
                                    error: syncStartDateError || syncStartDateBeforeDobError
                                }
                            }}
                            sx={{width: '100%'}}
                            required
                            format="D-MMM-YYYY"
                        />
                        <DatePicker
                            variant="fulfilled"
                            label="End date (optional)"
                            value={syncEndDate ? moment(syncEndDate) : null}
                            onChange={setSyncEndDate}
                            sx={{width: '100%'}}
                            minDate={syncStartDate ? moment(syncStartDate).clone().add(1, 'day') : null}
                            format="D-MMM-YYYY"
                        />
                        <FormControl fullWidth required>
                            <InputLabel id="sync-country-label">Country</InputLabel>
                            <Select
                                labelId="sync-country-label"
                                label="Country"
                                value={syncCountryId}
                                onChange={e => setSyncCountryId(e.target.value)}
                            >
                                {ukCountries.map((opt) => (
                                    <MenuItem key={opt.code} value={opt.code}>
                                        {opt.description}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            label="Note (optional)"
                            value={syncNote}
                            multiline
                            rows={2}
                            inputProps={{maxLength: 1000}}
                            helperText={`${(syncNote || '').length}/1000`}
                            onChange={e => setSyncNote(e.target.value)}
                            autoComplete="off"
                        />
                    </Box>
                </AdmicityDialog>
            </ConfirmationWrapper>
    );
};

FreeSchoolMealsFields.propTypes = {
    readonly: PropTypes.bool,
    onConfirmSuccess: PropTypes.func,
    onDataLoading: PropTypes.func
};

export default FreeSchoolMealsFields;
